// ============================================================================
// ChatWidget.jsx
// ----------------------------------------------------------------------------
// Floating PortfolioBot widget for anandpandey.me.
//
// Drop this file into:   src/components/ChatWidget/ChatWidget.jsx
// Then in src/App.js:
//   import ChatWidget from './components/ChatWidget/ChatWidget';
//   ...
//   <ChatWidget />   // place it anywhere inside <Router>, after <Navbar />
//
// Set REACT_APP_CHAT_API_URL in your .env (and on your host) to your
// Render backend URL, e.g. https://portfoliobot-backend.onrender.com
// ============================================================================

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Fab,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";

const BRAND = "#f9004d";
const API_URL =
  process.env.REACT_APP_CHAT_API_URL || "http://localhost:5050";

const SUGGESTIONS = [
  "Who is Anand?",
  "What projects has he built?",
  "How can I contact him?",
];

const WELCOME = {
  role: "assistant",
  content:
    "Hi! I'm PortfolioBot — an agentic AI assistant Anand built. Ask me about his projects, experience, skills, or how to reach him.",
};

const ChatWidget = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const userMessage = text.trim();
    if (!userMessage || loading) return;

    setError(null);
    const newMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      // Send only conversational history (skip the welcome message)
      const history = newMessages
        .slice(1, -1) // drop welcome AND the message we just added
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMessage, history }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Request failed");
      }

      setMessages([
        ...newMessages,
        { role: "assistant", content: data.reply },
      ]);
    } catch (err) {
      console.error(err);
      setError(
        err.message ||
          "Couldn't reach PortfolioBot. The backend may be waking up — try again in a few seconds."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    sendMessage(input);
  };

  // ----- Floating launcher button (closed state) ----------------------------
  if (!open) {
    return (
      <Tooltip title="Chat with PortfolioBot" placement="left">
        <Fab
          onClick={() => setOpen(true)}
          aria-label="Open chat"
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            backgroundColor: BRAND,
            color: "white",
            zIndex: 1300,
            boxShadow: "0 4px 20px rgba(249, 0, 77, 0.4)",
            "&:hover": {
              backgroundColor: BRAND,
              transform: "scale(1.06)",
            },
            transition: "transform 0.2s ease",
          }}
        >
          <ChatIcon />
        </Fab>
      </Tooltip>
    );
  }

  // ----- Chat panel (open state) --------------------------------------------
  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        bottom: { xs: 0, sm: 24 },
        right: { xs: 0, sm: 24 },
        width: { xs: "100%", sm: 380 },
        height: { xs: "100%", sm: 560 },
        maxHeight: { xs: "100%", sm: "calc(100vh - 48px)" },
        display: "flex",
        flexDirection: "column",
        borderRadius: { xs: 0, sm: "16px" },
        overflow: "hidden",
        zIndex: 1300,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: BRAND,
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SmartToyIcon />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.1 }}>
              PortfolioBot
            </Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.9 }}>
              Anand's agentic AI assistant
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => setOpen(false)}
          aria-label="Close chat"
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: 2,
          backgroundColor: "#fafafa",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {messages.map((m, i) => (
          <MessageBubble key={i} role={m.role} content={m.content} />
        ))}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 1 }}>
            <CircularProgress size={14} sx={{ color: BRAND }} />
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              PortfolioBot is thinking…
            </Typography>
          </Box>
        )}

        {error && (
          <Typography
            sx={{
              fontSize: 12,
              color: "#c62828",
              backgroundColor: "#ffebee",
              padding: 1,
              borderRadius: 1,
            }}
          >
            {error}
          </Typography>
        )}

        {/* Suggestion chips on first turn only */}
        {messages.length === 1 && !loading && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            {SUGGESTIONS.map((s) => (
              <Box
                key={s}
                onClick={() => sendMessage(s)}
                sx={{
                  cursor: "pointer",
                  border: `1px solid ${BRAND}`,
                  color: BRAND,
                  fontSize: 12,
                  padding: "4px 10px",
                  borderRadius: "14px",
                  "&:hover": {
                    backgroundColor: BRAND,
                    color: "white",
                  },
                }}
              >
                {s}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Input */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: 1.5,
          borderTop: "1px solid #eee",
          backgroundColor: "white",
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Ask about projects, skills, contact…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
          inputProps={{ maxLength: 1000 }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: BRAND },
            },
          }}
        />
        <IconButton
          type="submit"
          disabled={loading || !input.trim()}
          aria-label="Send message"
          sx={{
            backgroundColor: BRAND,
            color: "white",
            "&:hover": { backgroundColor: BRAND, opacity: 0.9 },
            "&.Mui-disabled": { backgroundColor: "#eee", color: "#aaa" },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Message bubble component
// ---------------------------------------------------------------------------
const MessageBubble = ({ role, content }) => {
  const isUser = role === "user";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
      }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          padding: "8px 12px",
          borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          backgroundColor: isUser ? BRAND : "white",
          color: isUser ? "white" : "#222",
          border: isUser ? "none" : "1px solid #eee",
          fontSize: 14,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {content}
      </Box>
    </Box>
  );
};

export default ChatWidget;
